import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { 
  Store, Delete, ArrowLeft, Building2, Loader2, CheckCircle2, 
  Smartphone, AlertTriangle, RefreshCw
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog";
import { useAuthStore, useThemeStore } from "@/store/useStore";
import { markSessionStart } from "@/lib/idle-timeout";
import { preloadPOSData } from "@/lib/pos-preloader";
import { cacheRegistration, getBrowserFingerprint } from "@/lib/device-registration";
import { isDemoMode } from "@/lib/demoMode";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PIN_LENGTH = 6;
const DEVICE_ID_KEY = "hostpoint_device_id";

// PIN dot component - moved outside main component for React best practices
const PinDot = ({ filled }) => (
  <div
    className={`w-3 h-3 rounded-full transition-all duration-200 ${
      filled ? "bg-primary scale-110" : "bg-muted border-2 border-border"
    }`}
  />
);

// Generate or retrieve a unique device ID
const getDeviceId = () => {
  let deviceId = localStorage.getItem(DEVICE_ID_KEY);
  if (!deviceId) {
    // Generate a fingerprint based on available browser/device info
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    ctx.textBaseline = "top";
    ctx.font = "14px Arial";
    ctx.fillText("HostPoint POS", 2, 2);
    const canvasData = canvas.toDataURL();
    
    const fingerprint = [
      navigator.userAgent,
      navigator.language,
      screen.width + "x" + screen.height,
      new Date().getTimezoneOffset(),
      canvasData.slice(-50)
    ].join("|");
    
    // Create a simple hash
    let hash = 0;
    for (let i = 0; i < fingerprint.length; i++) {
      const char = fingerprint.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    deviceId = "DEV-" + Math.abs(hash).toString(16).toUpperCase().padStart(8, "0") + 
               "-" + Date.now().toString(36).toUpperCase();
    localStorage.setItem(DEVICE_ID_KEY, deviceId);
  }
  return deviceId;
};

const getPlatform = () => {
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/.test(ua)) return "iOS";
  if (/Android/.test(ua)) return "Android";
  return "Web";
};

export default function PinLoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const demoMode = isDemoMode();
  const { setAuth, setCurrentOutlet, isAuthenticated } = useAuthStore();
  const { theme, toggleTheme } = useThemeStore();
  
  // Device registration state
  const [deviceId] = useState(() => getDeviceId());
  const [deviceStatus, setDeviceStatus] = useState(null); // null, "checking", "registered", "unregistered", "inactive"
  const [deviceConfig, setDeviceConfig] = useState(null);
  
  // Property code state (for unregistered devices or manual entry)
  const [propertyCode, setPropertyCode] = useState("");
  const [propertyVerified, setPropertyVerified] = useState(false);
  const [tenantName, setTenantName] = useState("");
  const [verifyingCode, setVerifyingCode] = useState(false);
  
  // Outlet and PIN state
  const [outlets, setOutlets] = useState([]);
  const [selectedOutlet, setSelectedOutlet] = useState("");
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  
  // Device registration dialog
  const [registerDialogOpen, setRegisterDialogOpen] = useState(false);
  const [registerForm, setRegisterForm] = useState({
    device_name: "",
    property_code: "",
    registration_pin: ""
  });
  const [registering, setRegistering] = useState(false);
  const registrationRequested = searchParams.get("register-device") === "1";

  const checkDeviceRegistration = useCallback(async () => {
    setDeviceStatus("checking");
    try {
      const response = await fetch(`${API_URL}/mobile-devices/check/${deviceId}`);
      const data = await response.json();
      cacheRegistration(data);
      
      if (data.registered && data.active) {
        setDeviceStatus("registered");
        setDeviceConfig(data);
        setTenantName(data.tenant_name);
        setOutlets(data.outlets || []);
        setPropertyCode(data.property_code);
        setPropertyVerified(true);
        
        // Auto-select default outlet or first outlet
        if (data.default_outlet_id) {
          setSelectedOutlet(data.default_outlet_id);
        } else if (data.outlets?.length > 0) {
          setSelectedOutlet(data.outlets[0].id);
        }
      } else if (data.registered && !data.active) {
        setDeviceStatus("inactive");
        setDeviceConfig(data);
      } else {
        setDeviceStatus("unregistered");
      }
    } catch (error) {
      setDeviceStatus("unregistered");
    }
  }, [deviceId]);

  // Check device registration status on mount
  useEffect(() => {
    // An authenticated back-office user can explicitly open this route to
    // register the current browser as a POS device.
    if (isAuthenticated && !registrationRequested) {
      navigate("/pos");
      return;
    }
    checkDeviceRegistration();
  }, [isAuthenticated, registrationRequested, navigate, checkDeviceRegistration]);

  useEffect(() => {
    if (registrationRequested && deviceStatus === "unregistered") {
      setRegisterDialogOpen(true);
    }
  }, [registrationRequested, deviceStatus]);

  const verifyPropertyCode = async () => {
    if (!propertyCode.trim()) {
      toast.error("Please enter a property code");
      return;
    }
    
    setVerifyingCode(true);
    try {
      const response = await fetch(`${API_URL}/public/outlets-by-code/${propertyCode.toUpperCase()}`);
      
      if (!response.ok) {
        let errorMessage = "Invalid property code";
        
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorMessage;
        } catch {
          if (response.status === 404) {
            errorMessage = "Property code not found. Please check and try again.";
          } else if (response.status >= 500) {
            errorMessage = "Server error. Please try again later.";
          }
        }
        
        throw new Error(errorMessage);
      }
      
      const data = await response.json();
      setTenantName(data.tenant_name);
      setOutlets(data.outlets || []);
      
      if (data.outlets && data.outlets.length > 0) {
        setSelectedOutlet(data.outlets[0].id);
      }
      
      setPropertyVerified(true);
      toast.success(`Welcome to ${data.tenant_name}`);
    } catch (error) {
      toast.error(error.message || "Invalid property code. Please check and try again.");
      setPropertyVerified(false);
    } finally {
      setVerifyingCode(false);
    }
  };

  const handleChangeProperty = () => {
    setPropertyVerified(false);
    setPin("");
    setOutlets([]);
    setSelectedOutlet("");
    setTenantName("");
    // If device was registered, go back to device mode
    if (deviceStatus === "registered") {
      checkDeviceRegistration();
    }
  };

  const handlePinInput = (digit) => {
    if (pin.length < PIN_LENGTH) {
      const newPin = pin + digit;
      setPin(newPin);
      
      // Auto-submit when PIN is complete
      if (newPin.length === PIN_LENGTH) {
        handleSubmit(newPin);
      }
    }
  };

  const handleDelete = () => {
    setPin(pin.slice(0, -1));
  };

  const handleClear = () => {
    setPin("");
  };

  const handleSubmit = async (submittedPin = pin) => {
    if (submittedPin.length !== PIN_LENGTH) {
      toast.error(`Please enter a ${PIN_LENGTH}-digit PIN`);
      return;
    }

    setLoading(true);
    try {
      // Use device PIN login if device is registered
      const endpoint = deviceStatus === "registered" 
        ? `${API_URL}/mobile-devices/pin-login`
        : `${API_URL}/auth/pin-login`;
      
      const body = deviceStatus === "registered"
        ? { device_id: deviceId, pin: submittedPin, outlet_id: selectedOutlet }
        : { pin: submittedPin, outlet_id: selectedOutlet, property_code: propertyCode.toUpperCase() };
      
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || "Invalid PIN");
      }

      // For device login, use outlet from response if available, otherwise use selected
      let outlet;
      if (data.outlet) {
        outlet = data.outlet;
      } else {
        outlet = outlets.find((o) => o.id === selectedOutlet) || { id: selectedOutlet, name: "Outlet" };
      }
      
      setAuth(data.user, data.access_token);
      setCurrentOutlet(outlet);
      
      // Mark session start for idle timeout tracking
      markSessionStart();
      
      // Preload POS data in background (non-blocking)
      preloadPOSData(data.user);
      
      toast.success(`Welcome, ${data.user.full_name}!`);
      setTimeout(() => navigate("/pos"), 100);
    } catch (error) {
      toast.error(error.message || "Login failed. Please try again.");
      setPin("");
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterDevice = async () => {
    if (!registerForm.device_name.trim()) {
      toast.error("Please enter a device name");
      return;
    }
    if (!registerForm.property_code.trim()) {
      toast.error("Please enter your property code");
      return;
    }
    if (!registerForm.registration_pin.trim() || registerForm.registration_pin.length !== 6) {
      toast.error("Please enter a valid 6-digit admin PIN");
      return;
    }

    setRegistering(true);
    try {
      // Get browser fingerprint for device recognition
      const browserFingerprint = await getBrowserFingerprint();
      
      const response = await fetch(`${API_URL}/mobile-devices/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          device_id: deviceId,
          device_name: registerForm.device_name.trim(),
          device_type: /Mobi|Android/i.test(navigator.userAgent) ? "mobile" : "desktop",
          property_code: registerForm.property_code.trim().toUpperCase(),
          registration_pin: registerForm.registration_pin,
          platform: getPlatform(),
          user_agent: navigator.userAgent,
          browser_fingerprint: browserFingerprint  // Include fingerprint for device recognition
        }),
      });

      // Handle non-OK responses with proper error messages
      if (!response.ok) {
        let errorMessage = "Registration failed";
        
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorMessage;
        } catch {
          // If JSON parsing fails, use status-based messages
          if (response.status === 404) {
            errorMessage = "Invalid property code. Please check and try again.";
          } else if (response.status === 401 || response.status === 403) {
            errorMessage = "Invalid admin PIN. Please check and try again.";
          } else if (response.status === 400) {
            errorMessage = "Invalid registration details. Please check all fields.";
          } else if (response.status >= 500) {
            errorMessage = "Server error. Please try again later.";
          }
        }
        
        throw new Error(errorMessage);
      }

      const data = await response.json();
      toast.success(`Device registered to ${data.tenant_name}!`);
      setRegisterDialogOpen(false);
      setRegisterForm({ device_name: "", property_code: "", registration_pin: "" });
      
      // Refresh device status
      await checkDeviceRegistration();
      if (isAuthenticated) {
        navigate("/pos", { replace: true });
      }
    } catch (error) {
      toast.error(error.message || "Failed to register device. Please try again.");
    } finally {
      setRegistering(false);
    }
  };

  // Show loading while checking device status
  if (deviceStatus === "checking") {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-background via-background to-muted">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="mt-3 text-muted-foreground">Checking device...</p>
        </div>
      </div>
    );
  }

  // Show inactive device message
  if (deviceStatus === "inactive") {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-background via-background to-muted">
        <Card className="max-w-sm w-full">
          <CardHeader className="text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-destructive/10 mx-auto mb-3">
              <AlertTriangle className="w-7 h-7 text-destructive" />
            </div>
            <CardTitle>Device Deactivated</CardTitle>
            <CardDescription>
              This device has been deactivated by an administrator.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center text-sm text-muted-foreground">
            <p>Please contact your manager to reactivate this device or use a different device.</p>
          </CardContent>
          <CardFooter className="flex flex-col gap-2">
            <Button variant="outline" className="w-full" onClick={checkDeviceRegistration}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Retry
            </Button>
            <Button variant="ghost" className="w-full" onClick={() => navigate("/login")}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back Office Login
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-background via-background to-muted">
      <div className="w-full max-w-sm">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/10 mb-3">
            <Store className="w-7 h-7 text-primary" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground font-[Manrope]">
            HostPoint POS
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">Point of Sale Login</p>
        </div>

        <Card className="border-border/50 shadow-xl">
          <CardHeader className="space-y-1 pb-3">
            <CardTitle className="text-lg font-[Manrope] text-center">
              {propertyVerified ? tenantName : "Enter Property Code"}
            </CardTitle>
            <CardDescription className="text-center text-sm">
              {propertyVerified 
                ? `Enter your ${PIN_LENGTH}-digit PIN` 
                : deviceStatus === "registered" 
                  ? "Device registered - loading..."
                  : "Your property code is in your welcome email"
              }
            </CardDescription>
            
            {/* Device Registration Badge */}
            {deviceStatus === "registered" && (
              <div className="flex items-center justify-center gap-2 pt-2">
                <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs">
                  <Smartphone className="h-3 w-3" />
                  <span>Registered Device</span>
                </div>
              </div>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {!propertyVerified ? (
              /* Property Code Entry */
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="propertyCode" className="flex items-center gap-2 text-sm">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    Property Code
                  </Label>
                  <Input
                    id="propertyCode"
                    placeholder="e.g., DIANI_PLATE"
                    value={propertyCode}
                    onChange={(e) => setPropertyCode(e.target.value.toUpperCase())}
                    className="uppercase text-center text-lg font-mono tracking-wider"
                    onKeyDown={(e) => e.key === "Enter" && verifyPropertyCode()}
                    data-testid="property-code-input"
                  />
                </div>
                <Button 
                  className="w-full" 
                  onClick={verifyPropertyCode}
                  disabled={verifyingCode || !propertyCode.trim()}
                  data-testid="verify-property-btn"
                >
                  {verifyingCode ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="mr-2 h-4 w-4" />
                      Continue
                    </>
                  )}
                </Button>
              </div>
            ) : (
              /* PIN Entry */
              <div className="space-y-4">
                {/* Property Badge */}
                <div className="flex items-center justify-between bg-muted/50 p-2 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium">{propertyCode}</span>
                  </div>
                  {deviceStatus !== "registered" && (
                    <Button variant="ghost" size="sm" onClick={handleChangeProperty}>
                      Change
                    </Button>
                  )}
                </div>

                {/* Outlet Selection */}
                {outlets.length > 0 && (
                  <div>
                    <Label className="text-sm mb-1.5 block">Select Outlet</Label>
                    <Select value={selectedOutlet} onValueChange={setSelectedOutlet}>
                      <SelectTrigger data-testid="outlet-select">
                        <SelectValue placeholder="Select Outlet" />
                      </SelectTrigger>
                      <SelectContent>
                        {outlets.map((outlet) => (
                          <SelectItem key={outlet.id} value={outlet.id}>
                            {outlet.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* PIN Display */}
                <div className="flex justify-center gap-2 py-3" data-testid="pin-dots">
                  {Array.from({ length: PIN_LENGTH }).map((_, i) => (
                    <PinDot key={i} filled={pin.length > i} />
                  ))}
                </div>

                {/* Number Pad */}
                <div className="grid grid-cols-3 gap-2">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                    <Button
                      key={num}
                      variant="outline"
                      className="h-12 text-lg font-semibold hover:bg-accent active:scale-95 transition-transform"
                      onClick={() => handlePinInput(num.toString())}
                      disabled={loading}
                      data-testid={`pin-btn-${num}`}
                    >
                      {num}
                    </Button>
                  ))}
                  <Button
                    variant="outline"
                    className="h-12 text-xs text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                    onClick={handleClear}
                    disabled={loading}
                    data-testid="pin-clear"
                  >
                    Clear
                  </Button>
                  <Button
                    variant="outline"
                    className="h-12 text-lg font-semibold hover:bg-accent active:scale-95 transition-transform"
                    onClick={() => handlePinInput("0")}
                    disabled={loading}
                    data-testid="pin-btn-0"
                  >
                    0
                  </Button>
                  <Button
                    variant="outline"
                    className="h-12 hover:bg-destructive/10 hover:text-destructive"
                    onClick={handleDelete}
                    disabled={loading}
                    data-testid="pin-delete"
                  >
                    <Delete className="h-4 w-4" />
                  </Button>
                </div>

                {loading && (
                  <div className="text-center text-muted-foreground text-sm">
                    <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                    Verifying PIN...
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Builders Contest — demo POS access (only when opened with ?demo=1) */}
        {demoMode && (
        <div className="mt-4 rounded-lg border border-primary/40 bg-primary/5 p-3 text-sm" data-testid="contest-demo-pin">
          <div className="flex items-center gap-2 mb-1.5">
            <Store className="h-4 w-4 text-primary" />
            <span className="font-semibold text-foreground">Contest Judge Demo Access</span>
          </div>
          <div className="flex justify-between"><span className="text-muted-foreground">Property Code</span><span className="font-mono font-semibold">DIANI_PLATE</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">POS PIN</span><span className="font-mono font-semibold">246810</span></div>
          <p className="text-xs text-muted-foreground mt-1.5">Register this device with the property code + PIN, then log in with the PIN.</p>
        </div>
        )}

        <div className="mt-4 flex flex-col gap-2">
          <Button
            variant="ghost"
            className="w-full"
            onClick={() => navigate("/login")}
            data-testid="goto-admin-login"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back Office Login
          </Button>
          
          {/* Device Registration Link - Only show for unregistered devices */}
          {deviceStatus === "unregistered" && (
            <Button
              variant="link"
              size="sm"
              className="text-xs"
              onClick={() => setRegisterDialogOpen(true)}
              data-testid="register-device-link"
            >
              <Smartphone className="mr-1.5 h-3.5 w-3.5" />
              Register this Device
            </Button>
          )}
          
          <Button variant="ghost" size="sm" onClick={toggleTheme} className="mx-auto text-xs">
            {theme === "dark" ? "Light Mode" : "Dark Mode"}
          </Button>
        </div>
      </div>

      {/* Device Registration Dialog */}
      <Dialog open={registerDialogOpen} onOpenChange={setRegisterDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Smartphone className="h-5 w-5 text-primary" />
              Register Device
            </DialogTitle>
            <DialogDescription>
              Link this device to your property for faster login. You&apos;ll need an admin PIN to authorize.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="device_name">Device Name</Label>
              <Input
                id="device_name"
                placeholder="e.g., Counter 1 Tablet"
                value={registerForm.device_name}
                onChange={(e) => setRegisterForm({ ...registerForm, device_name: e.target.value })}
                data-testid="register-device-name"
              />
              <p className="text-xs text-muted-foreground">
                A friendly name to identify this device
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="register_property_code">Property Code</Label>
              <Input
                id="register_property_code"
                placeholder="e.g., DIANI_PLATE"
                value={registerForm.property_code}
                onChange={(e) => setRegisterForm({ ...registerForm, property_code: e.target.value.toUpperCase() })}
                className="uppercase"
                data-testid="register-property-code"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="registration_pin">Admin PIN</Label>
              <Input
                id="registration_pin"
                type="password"
                placeholder="Enter 6-digit admin PIN"
                maxLength={6}
                value={registerForm.registration_pin}
                onChange={(e) => setRegisterForm({ ...registerForm, registration_pin: e.target.value.replace(/\D/g, "") })}
                data-testid="register-admin-pin"
              />
              <p className="text-xs text-muted-foreground">
                An administrator must authorize device registration
              </p>
            </div>
            
            <div className="pt-2 px-3 py-2 bg-muted/50 rounded-md">
              <p className="text-xs text-muted-foreground">
                <strong>Device ID:</strong>{" "}
                <span className="font-mono">{deviceId}</span>
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                <strong>Platform:</strong> {getPlatform()}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRegisterDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleRegisterDevice} 
              disabled={registering}
              data-testid="submit-device-registration"
            >
              {registering ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Registering...
                </>
              ) : (
                "Register Device"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
